/**
 * collection-products.js
 *
 * Handles:
 *  1. Filter drawer open / close (with overlay + focus trap)
 *  2. Filter accordion groups (toggle expand/collapse)
 *  3. Filter form auto-submit on checkbox change
 *  4. Sort-by select auto-submit
 *  5. URL-based state (no page reload needed when JS handles pushState — optional)
 *  6. Keyboard accessibility (Escape closes drawer)
 */

(function () {
  'use strict';

  // ── Selectors ──────────────────────────────────────────────────────────────
  const DRAWER_ID         = 'filter-drawer';
  const OVERLAY_ID        = 'filter-drawer-overlay';
  const OPEN_BTN_ID       = 'filter-drawer-open';
  const CLOSE_BTN_ID      = 'filter-drawer-close';
  const FACETS_FORM_ID    = 'FacetFiltersForm';
  const PRODUCT_GRID_ID   = 'product-grid';

  let drawer, overlay, openBtn, closeBtn, facetsForm, sortSelect;
  let lastFocused = null;

  // ── Init ───────────────────────────────────────────────────────────────────
  function init() {
    drawer    = document.getElementById(DRAWER_ID);
    overlay   = document.getElementById(OVERLAY_ID);
    openBtn   = document.getElementById(OPEN_BTN_ID);
    closeBtn  = document.getElementById(CLOSE_BTN_ID);
    facetsForm = document.getElementById(FACETS_FORM_ID);
    sortSelect = document.querySelector('.toolbar-sort__select');

    if (!drawer) return;

    // Open/close drawer
    if (openBtn)  openBtn.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay)  overlay.addEventListener('click', closeDrawer);

    // Keyboard: Escape to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) closeDrawer();
    });

    // Accordion toggles inside drawer
    drawer.querySelectorAll('.filter-group__toggle').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        var contentId = toggle.getAttribute('aria-controls');
        var content = document.getElementById(contentId);
        if (content) content.hidden = expanded;
      });
    });

    // Auto-submit filter form on checkbox change
    if (facetsForm) {
      facetsForm.querySelectorAll('.filter-checkbox__input').forEach(function (checkbox) {
        checkbox.addEventListener('change', debounce(submitFilters, 300));
      });

      // Price range: submit on blur of price inputs
      facetsForm.querySelectorAll('.filter-price-range__input').forEach(function (input) {
        input.addEventListener('change', debounce(submitFilters, 400));
      });

      // Prevent default form submit (we handle it programmatically)
      facetsForm.addEventListener('submit', function (e) {
        e.preventDefault();
        submitFilters();
      });
    }

    // Auto-submit sort select
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        applySort(sortSelect.value);
      });
    }
  }

  // ── Drawer helpers ─────────────────────────────────────────────────────────
  function isOpen() {
    return drawer && drawer.classList.contains('is-open');
  }

  function openDrawer() {
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.classList.add('is-active');
      overlay.setAttribute('aria-hidden', 'false');
    }
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    // Move focus into drawer
    requestAnimationFrame(function () {
      var firstFocusable = getFocusableElements(drawer)[0];
      if (firstFocusable) firstFocusable.focus();
    });
    // Trap focus
    drawer.addEventListener('keydown', trapFocus);
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) {
      overlay.classList.remove('is-active');
      overlay.setAttribute('aria-hidden', 'true');
    }
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    drawer.removeEventListener('keydown', trapFocus);
    document.body.style.overflow = '';
    if (lastFocused) {
      lastFocused.focus();
      lastFocused = null;
    }
  }

  // Focus trap within drawer
  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = getFocusableElements(drawer);
    if (!focusable.length) return;
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  }

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return !el.closest('[hidden]'); });
  }

  // ── Filter form submission ─────────────────────────────────────────────────
  function submitFilters() {
    if (!facetsForm) return;
    var formData = new FormData(facetsForm);
    var params   = new URLSearchParams(formData).toString();
    var url      = window.location.pathname + (params ? '?' + params : '');

    // Native navigation (simplest, works with any theme setup)
    window.location.href = url;
  }

  // ── Sort ───────────────────────────────────────────────────────────────────
  function applySort(value) {
    var url    = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    // Preserve current filters
    window.location.href = url.toString();
  }

  // ── Utility ────────────────────────────────────────────────────────────────
  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
